// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { DynProperty, PairColor } from '@shared/commons/DynProperty';
import { Point, Rectangle, Size } from '@shared/commons/Geometry';
import { SolidBrush } from '@shared/commons/Graphics';
import { Resources } from '@shared/localizations/Resources';
import { MouseButtons } from '@front/controls/UtilsClasses/ControlsUtils';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { TerceraChartBaseRenderer } from '../TerceraChartBaseRenderer';
import { InfoWindowDockingType } from './InfoWindowDockingType';
import { InfoWindowItem } from './InfoWindowItem';
export class TerceraChartBaseInfoWindowRenderer extends TerceraChartBaseRenderer {
    constructor() {
        super(...arguments);
        this._infoWindowItems = [];
        this._dockingState = InfoWindowDockingType.Top;
        this._isMoving = false;
        this._isUpdateInfoWindow = true;
        this._prevMousePosition = Point.Empty();
        this._newMousePosition = Point.Empty();
        this._lastDataX = NaN;
        this._lastDataY = NaN;
        this._undockColumnsNumber = 2;
        this.font = ThemeManager.Fonts.Font_12F_regular;
        this.Rectangle = Rectangle.Empty();
        this.BackgroundColor = ThemeManager.colors.chartInfoWindowBackColor;
        this.ForegroundColor = ThemeManager.colors.chartInfoWindowTitleForeground;
        this.isNeedForceUpdate = false;
        this.PADDING_LEFT = 8;
        this.PADDING_RIGHT = 8;
        this.PADDING_TOP = 10;
        this.PADDING_BOTTOM = 8;
        this.UNDOCK_PADDING_BOTTOM = 12;
        this.HORIZONTAL_OFFSET = 16;
        this.VERTICAL_OFFSET = 16;
        this.LABEL_OFFSET = 8;
    }
    initializeItems(ids) {
        for (let i = 0; i < ids.length; i++) {
            this._infoWindowItems.push(new InfoWindowItem(ids[i]));
        }
    }
    get valuesNumber() {
        let totalLength = 0;
        this._infoWindowItems.forEach((item) => {
            if (item.isVisible) {
                totalLength += item.formattedValues.length;
            }
        });
        return totalLength;
    }
    get undockColumnWidth() {
        var _a;
        return ((_a = this.Rectangle) === null || _a === void 0 ? void 0 : _a.Width) / this._undockColumnsNumber;
    }
    get undockColumnsNumber() {
        return this._undockColumnsNumber;
    }
    get undockRowsNumber() {
        return Math.ceil(this.valuesNumber / this._undockColumnsNumber);
    }
    get IsDocked() {
        return this._dockingState !== InfoWindowDockingType.None;
    }
    get IsDockedTop() {
        return this._dockingState === InfoWindowDockingType.Top;
    }
    get IsDockedBottom() {
        return this._dockingState === InfoWindowDockingType.Bottom;
    }
    // #region Overrides
    Draw(gr, window, windowsContainer, advParams) {
        if (!this.Visible) {
            return;
        }
        if (this._isUpdateInfoWindow) {
            this.updateRectangle(gr, window);
            this._isUpdateInfoWindow = false;
        }
        const infoWindowRectangle = this.Rectangle;
        gr.save();
        gr.beginPath();
        gr.rect(infoWindowRectangle.X, infoWindowRectangle.Y, infoWindowRectangle.Width, infoWindowRectangle.Height);
        gr.clip();
        const backgroundBrush = new SolidBrush(this.BackgroundColor);
        gr.FillRect(backgroundBrush, infoWindowRectangle.X, infoWindowRectangle.Y, infoWindowRectangle.Width, infoWindowRectangle.Height);
        const location = new Point(infoWindowRectangle.X + this.PADDING_LEFT, infoWindowRectangle.Y + this.PADDING_TOP);
        const labelForegroundBrush = new SolidBrush(this.ForegroundColor);
        let maxLabelWidth = 0;
        for (let i = 0; i < this._infoWindowItems.length; i++) {
            const infoWindowItem = this._infoWindowItems[i];
            if (!infoWindowItem.isVisible) {
                continue;
            }
            const label = isValidString(infoWindowItem.label) ? `${infoWindowItem.label}:` : '';
            const labelSize = gr.GetTextSize(label, this.font, true);
            maxLabelWidth = Math.max(maxLabelWidth, labelSize.width);
        }
        let counter = 0;
        for (let i = 0; i < this._infoWindowItems.length; i++) {
            const infoWindowItem = this._infoWindowItems[i];
            if (!infoWindowItem.isVisible) {
                continue;
            }
            const label = isValidString(infoWindowItem.label) ? `${infoWindowItem.label}:` : '';
            const values = infoWindowItem.formattedValues;
            const valuesForegroundColors = this.getItemValueForegroundColors(infoWindowItem);
            const labelSize = gr.GetTextSize(label, this.font, true);
            if (isValidString(label)) {
                gr.DrawString(label, this.font, labelForegroundBrush, location.X, location.Y);
            }
            for (let j = 0; j < values.length; j++) {
                const value = values[j];
                const valueForegroundBrush = isValidString(valuesForegroundColors[j])
                    ? new SolidBrush(valuesForegroundColors[j])
                    : labelForegroundBrush;
                const valueSize = gr.GetTextSize(values[j], this.font, true);
                if (this.IsDocked) {
                    const xOffset = j === 0 ? labelSize.width + this.LABEL_OFFSET : 0;
                    location.X += xOffset;
                    gr.DrawString(value, this.font, valueForegroundBrush, location.X, location.Y);
                    location.X += valueSize.width + this.HORIZONTAL_OFFSET;
                }
                else {
                    const x = location.X + maxLabelWidth + this.LABEL_OFFSET;
                    gr.DrawString(value, this.font, valueForegroundBrush, x, location.Y);
                    const rowNum = ++counter % this.undockRowsNumber;
                    const colNum = Math.floor(counter / this.undockRowsNumber);
                    location.X = colNum * this.undockColumnWidth + infoWindowRectangle.X + this.PADDING_LEFT;
                    location.Y += valueSize.height + this.VERTICAL_OFFSET;
                    if (rowNum === 0) {
                        location.Y = infoWindowRectangle.Y + this.PADDING_TOP;
                    }
                }
            }
        }
        gr.restore();
    }
    Localize() {
        super.Localize();
        this.localizeItems();
    }
    ThemeChanged() {
        super.ThemeChanged();
        this.BackgroundColor = ThemeManager.colors.chartInfoWindowBackColor;
        this.ForegroundColor = ThemeManager.colors.chartInfoWindowTitleForeground;
    }
    Properties() {
        const props = super.Properties();
        // #region hidden
        props.push(new DynProperty('infowindow_Docked', this._dockingState, DynProperty.INTEGER, DynProperty.HIDDEN_GROUP));
        props.push(new DynProperty('infowindow_fx', this.Rectangle.X, DynProperty.INTEGER, DynProperty.HIDDEN_GROUP));
        props.push(new DynProperty('infowindow_fy', this.Rectangle.Y, DynProperty.INTEGER, DynProperty.HIDDEN_GROUP));
        // #endregion
        let sortIndex = 0;
        const infoWindowItems = this._infoWindowItems;
        const infoWindowItemsProps = [];
        const visibilitySeparatorGroup = '#0#' + Resources.getResource('property.VisibilitySeparatorGroup');
        const viewSeparatorGroup = '#1#' + Resources.getResource('property.ViewSeparatorGroup');
        const colorsSeparatorGroup = '#2#' + Resources.getResource('property.ColorsSeparatorGroup');
        let prop = new DynProperty('infowindow_visible', this.Visible, DynProperty.BOOLEAN, this.getDynPropertyGroup());
        prop.sortIndex = sortIndex++;
        prop.separatorGroup = visibilitySeparatorGroup;
        prop.assignedProperty = [];
        for (let i = 0; i < infoWindowItems.length; i++) {
            const infoWindowItem = infoWindowItems[i];
            const propName = `infoWindowItem_${infoWindowItem.id}`;
            prop.assignedProperty.push(propName);
            const dp = new DynProperty(propName, infoWindowItem.isVisible, DynProperty.BOOLEAN, this.getDynPropertyGroup());
            dp.propertyComment = infoWindowItem.label;
            dp.sortIndex = sortIndex++;
            dp.separatorGroup = viewSeparatorGroup;
            dp.enabled = this.Visible;
            infoWindowItemsProps.push(dp);
        }
        props.push(prop);
        props.push(...infoWindowItemsProps);
        prop = new DynProperty('infoWindow_fore_back_color', new PairColor(this.ForegroundColor, this.BackgroundColor, 'Fore', 'Back'), DynProperty.PAIR_COLOR, this.getDynPropertyGroup());
        prop.sortIndex = sortIndex++;
        prop.separatorGroup = colorsSeparatorGroup;
        prop.enabled = this.Visible;
        props.push(prop);
        return props;
    }
    callBack(properties) {
        super.callBack(properties);
        let dp = DynProperty.getPropertyByName(properties, 'infowindow_Docked');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this._dockingState = dp.value;
        }
        dp = DynProperty.getPropertyByName(properties, 'infowindow_fx');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this.Rectangle.X = dp.value;
        }
        dp = DynProperty.getPropertyByName(properties, 'infowindow_fy');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this.Rectangle.Y = dp.value;
        }
        dp = DynProperty.getPropertyByName(properties, 'infowindow_visible');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this.Visible = dp.value;
        }
        const infoWindowItems = this._infoWindowItems;
        for (let i = 0; i < infoWindowItems.length; i++) {
            const infoWindowItem = infoWindowItems[i];
            dp = DynProperty.getPropertyByName(properties, `infoWindowItem_${infoWindowItem.id}`);
            if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
                infoWindowItem.isVisible = dp.value;
            }
        }
        dp = DynProperty.getPropertyByName(properties, 'infoWindow_fore_back_color');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            const pairColor = dp.value;
            this.ForegroundColor = pairColor.Color1;
            this.BackgroundColor = pairColor.Color2;
        }
    }
    ProcessMouseDown(e) {
        let processed = super.ProcessMouseDown(e);
        if (!this.Visible || e.Button !== MouseButtons.Left) {
            return processed;
        }
        if (this.Rectangle.Contains(e.Location.X, e.Location.Y)) {
            this._prevMousePosition = e.Location;
            this._isMoving = true;
            processed = true;
        }
        else {
            this._prevMousePosition = Point.Empty();
            this._isMoving = false;
        }
        return processed || this._isMoving;
    }
    ProcessMouseMove(e) {
        const processed = super.ProcessMouseMove(e);
        if (!this.Visible || isNullOrUndefined(e.window)) {
            return processed;
        }
        const dataX = e.window.PointsConverter.GetDataX(e.Location.X);
        const dataY = e.window.PointsConverter.GetDataY(e.Location.Y);
        this.updateItems(dataX, dataY);
        if (this._isMoving) {
            this._newMousePosition = e.Location;
        }
        this._isUpdateInfoWindow = true;
    }
    ProcessMouseUp(e) {
        let processed = super.ProcessMouseUp(e);
        if (this.Visible && this._isMoving) {
            this._prevMousePosition = this._newMousePosition = Point.Empty();
            this._isMoving = false;
            processed = true;
        }
        return processed;
    }
    IsNeedForceUpdate() {
        let isUpdated = false;
        if (this.isNeedForceUpdate) {
            this.isNeedForceUpdate = false;
            if (!isNaN(this._lastDataX) && !isNaN(this._lastDataY)) {
                this.updateItems(this._lastDataX, this._lastDataY);
                this._isUpdateInfoWindow = true;
                isUpdated = true;
            }
        }
        return isUpdated;
    }
    // #endregion
    // #region Protected
    getItemById(id) {
        for (let i = 0; i < this._infoWindowItems.length; i++) {
            if (this._infoWindowItems[i].id === id) {
                return this._infoWindowItems[i];
            }
        }
        return null;
    }
    updateItem(item, dataX, dataY) { }
    localizeItem(item) { }
    getItemValueForegroundColors(item) {
        return [''];
    }
    getDynPropertyGroup() {
        return DynProperty.DATA_BOX_GROUP;
    }
    // #endregion
    updateItems(dataX, dataY) {
        this._lastDataX = dataX;
        this._lastDataY = dataY;
        for (let i = 0; i < this._infoWindowItems.length; i++) {
            this.updateItem(this._infoWindowItems[i], dataX, dataY);
        }
    }
    localizeItems() {
        for (let i = 0; i < this._infoWindowItems.length; i++) {
            this.localizeItem(this._infoWindowItems[i]);
        }
    }
    updateRectangle(gr, window) {
        const windowClientRectangle = window.ClientRectangle;
        let infoWindowRectangle = new Rectangle(this.Rectangle.X, this.Rectangle.Y, this.Rectangle.Width, this.Rectangle.Height);
        if (this._isMoving) {
            const xDelta = this._newMousePosition.X - this._prevMousePosition.X;
            const yDelta = this._newMousePosition.Y - this._prevMousePosition.Y;
            const newInfoWindowRectangle = new Rectangle(infoWindowRectangle.X + xDelta, infoWindowRectangle.Y + yDelta, infoWindowRectangle.Width, infoWindowRectangle.Height);
            if (infoWindowRectangle.Top() > windowClientRectangle.Top() &&
                newInfoWindowRectangle.Top() <= windowClientRectangle.Top()) {
                this._dockingState = InfoWindowDockingType.Top;
            }
            else if (infoWindowRectangle.Bottom() < windowClientRectangle.Bottom() &&
                newInfoWindowRectangle.Bottom() >= windowClientRectangle.Bottom()) {
                this._dockingState = InfoWindowDockingType.Bottom;
            }
            else if (this.IsDockedTop && newInfoWindowRectangle.Top() > windowClientRectangle.Top()) {
                this._dockingState = InfoWindowDockingType.None;
                newInfoWindowRectangle.X = this._newMousePosition.X;
            }
            else if (this.IsDockedBottom && newInfoWindowRectangle.Bottom() < windowClientRectangle.Bottom()) {
                this._dockingState = InfoWindowDockingType.None;
                newInfoWindowRectangle.X = this._newMousePosition.X;
            }
            infoWindowRectangle = newInfoWindowRectangle;
            this._prevMousePosition = this._newMousePosition;
        }
        const infoWindowSize = this.calculateInfoWindowSize(gr);
        infoWindowRectangle.Width = infoWindowSize.Width;
        infoWindowRectangle.Height = infoWindowSize.Height;
        switch (this._dockingState) {
            case InfoWindowDockingType.Top:
                infoWindowRectangle.X = windowClientRectangle.X;
                infoWindowRectangle.Y = windowClientRectangle.Y;
                break;
            case InfoWindowDockingType.Bottom:
                infoWindowRectangle.X = windowClientRectangle.X;
                infoWindowRectangle.Y = windowClientRectangle.Bottom() - infoWindowSize.Height;
                break;
        }
        this.Rectangle = this.correctInfoWindowRectangle(infoWindowRectangle, windowClientRectangle);
    }
    calculateInfoWindowSize(gr) {
        const size = new Size(0, 0);
        const isDocked = this.IsDocked;
        let maxLabelWidth = 0;
        let maxValueWidth = 0;
        for (const infoWindowItem of this._infoWindowItems) {
            if (!infoWindowItem.isVisible) {
                continue;
            }
            const label = isValidString(infoWindowItem.label) ? `${infoWindowItem.label}:` : '';
            const values = infoWindowItem.formattedValues;
            const labelSize = gr.GetTextSize(label, this.font, true);
            for (let j = 0; j < values.length; j++) {
                const value = values[j];
                const valueSize = gr.GetTextSize(value, this.font, true);
                if (isDocked) {
                    const offset = j === 0 ? labelSize.width + this.LABEL_OFFSET : 0;
                    const textSize = new Size(offset + valueSize.width, Math.max(labelSize.height, valueSize.height));
                    size.Width += textSize.Width + this.HORIZONTAL_OFFSET;
                    size.Height = Math.max(size.Height, textSize.Height);
                }
                else {
                    maxLabelWidth = Math.max(maxLabelWidth, labelSize.width);
                    maxValueWidth = Math.max(maxValueWidth, valueSize.width);
                    size.Height += Math.max(labelSize.height, valueSize.height) + this.VERTICAL_OFFSET;
                }
            }
        }
        if (isDocked) {
            size.Width -= this.HORIZONTAL_OFFSET;
        }
        else {
            size.Width = (maxLabelWidth + this.LABEL_OFFSET + maxValueWidth) * this.undockColumnsNumber;
            size.Height /= this.undockColumnsNumber;
        }
        size.Width += this.PADDING_LEFT + this.PADDING_RIGHT;
        size.Height += this.PADDING_TOP + this.PADDING_BOTTOM;
        if (!isDocked && this.valuesNumber % this.undockColumnsNumber === 0) {
            size.Height -= this.UNDOCK_PADDING_BOTTOM;
        }
        return size;
    }
    correctInfoWindowRectangle(infoWindowRectangle, windowClientRectangle) {
        const rect = infoWindowRectangle;
        if (rect.X < windowClientRectangle.X) {
            rect.X = windowClientRectangle.X;
        }
        if (rect.Y < windowClientRectangle.Y) {
            rect.Y = windowClientRectangle.Y;
        }
        if (rect.Right() > windowClientRectangle.Right()) {
            if (windowClientRectangle.Width >= infoWindowRectangle.Width) {
                const delta = rect.Right() - windowClientRectangle.Right();
                rect.X -= delta;
            }
            else {
                infoWindowRectangle.Width = windowClientRectangle.Width;
            }
        }
        if (rect.Bottom() > windowClientRectangle.Bottom()) {
            if (windowClientRectangle.Height >= infoWindowRectangle.Height) {
                const delta = rect.Bottom() - windowClientRectangle.Bottom();
                rect.Y -= delta;
            }
            else {
                infoWindowRectangle.Height = windowClientRectangle.Height;
            }
        }
        return rect;
    }
}
